import type { ComponentProps } from 'react'

import type { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { CompensationsValueObjectsPayrollRunStatusEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

export type StatusVariant = NonNullable<ComponentProps<typeof ResourceHeader>['status']>['variant']

export type LegacyPayrollRunStatus = Extract<
  CompensationsValueObjectsPayrollRunStatusEnum,
  'opened' | 'closed'
>

export type PayrollRunStatus = Exclude<
  CompensationsValueObjectsPayrollRunStatusEnum,
  LegacyPayrollRunStatus
>

export const LegacyPayrollRunStatus = {
  OPENED: 'opened',
  CLOSED: 'closed',
} satisfies Record<string, LegacyPayrollRunStatus>

export const PayrollRunStatus = {
  OPEN: 'open',
  IN_PREPARATION: 'in_preparation',
  CALCULATION: 'calculation',
  IN_REVIEW: 'in_review',
  // TODO: this is a mistake on the CompensationsValueObjectsPayrollRunStatusEnum type, should be complete instead of completed
  COMPLETED: 'completed',
} satisfies Record<string, PayrollRunStatus>

export type StatusConfig = {
  variant: StatusVariant
}

const PAYROLL_RUN_CYCLE_STATUS_CONFIG: Record<PayrollRunStatus, StatusConfig> = {
  [PayrollRunStatus.OPEN]: {
    variant: 'neutral',
  },
  [PayrollRunStatus.IN_PREPARATION]: {
    variant: 'info',
  },
  [PayrollRunStatus.CALCULATION]: {
    variant: 'info',
  },
  [PayrollRunStatus.IN_REVIEW]: {
    variant: 'warning',
  },
  [PayrollRunStatus.COMPLETED]: {
    variant: 'positive',
  },
}

export type PayrollRunCycleStatus = keyof typeof PAYROLL_RUN_CYCLE_STATUS_CONFIG

const LEGACY_STATUS_MAPPING = {
  [LegacyPayrollRunStatus.OPENED]: PayrollRunStatus.OPEN,
  [LegacyPayrollRunStatus.CLOSED]: PayrollRunStatus.COMPLETED,
} as const

const isPayrollRunCycleStatus = (status: string): status is PayrollRunCycleStatus => {
  return status in PAYROLL_RUN_CYCLE_STATUS_CONFIG
}

const isLegacyPayrollRunStatus = (status: string): status is keyof typeof LEGACY_STATUS_MAPPING => {
  return status in LEGACY_STATUS_MAPPING
}

/**
 * Gets the raw status configuration for styling based on the status value
 */
export const getRawStatusConfig = (status: string | undefined): StatusConfig => {
  if (!status) {
    return PAYROLL_RUN_CYCLE_STATUS_CONFIG[PayrollRunStatus.OPEN]
  }

  if (isPayrollRunCycleStatus(status)) {
    return PAYROLL_RUN_CYCLE_STATUS_CONFIG[status]
  }

  if (isLegacyPayrollRunStatus(status)) {
    const mappedStatus = LEGACY_STATUS_MAPPING[status]
    return PAYROLL_RUN_CYCLE_STATUS_CONFIG[mappedStatus]
  }

  return PAYROLL_RUN_CYCLE_STATUS_CONFIG[PayrollRunStatus.OPEN]
}

/**
 * Gets the variant for a status tag based on the status value
 */
export const getPayrollRunStatusVariant = (status: string | undefined): StatusVariant => {
  return getRawStatusConfig(status).variant
}

/**
 * Gets the translated display text for a payroll run status
 */
export const getPayrollRunStatusText = (status: string | undefined): string => {
  let normalizedStatus = status

  if (!normalizedStatus) {
    normalizedStatus = PayrollRunStatus.OPEN
  }

  if (isLegacyPayrollRunStatus(normalizedStatus)) {
    normalizedStatus = LEGACY_STATUS_MAPPING[normalizedStatus]
  }

  switch (normalizedStatus) {
    case PayrollRunStatus.OPEN:
      return i18n.t('compensations.PayrollRunCompensationsList.status.open')
    case PayrollRunStatus.IN_PREPARATION:
      return i18n.t('compensations.PayrollRunCompensationsList.status.in_preparation')
    case PayrollRunStatus.CALCULATION:
      return i18n.t('compensations.PayrollRunCompensationsList.status.calculation')
    case PayrollRunStatus.IN_REVIEW:
      return i18n.t('compensations.PayrollRunCompensationsList.status.in_review')
    case PayrollRunStatus.COMPLETED:
      return i18n.t('compensations.PayrollRunCompensationsList.status.complete')
    default:
      return i18n.t('compensations.PayrollRunCompensationsList.status.open')
  }
}

/**
 * Gets the next status in the payroll run lifecycle based on the current status.
 * Follows the progression: open -> in_preparation -> calculation -> in_review -> completed
 */
export const getNextPayrollRunStatus = (currentStatus: string | undefined): PayrollRunStatus => {
  if (!currentStatus) {
    return PayrollRunStatus.IN_PREPARATION
  }

  let normalizedStatus = currentStatus

  if (currentStatus === LegacyPayrollRunStatus.OPENED) {
    normalizedStatus = PayrollRunStatus.OPEN
  }

  if (currentStatus === LegacyPayrollRunStatus.CLOSED) {
    normalizedStatus = PayrollRunStatus.COMPLETED
  }

  switch (normalizedStatus) {
    case PayrollRunStatus.OPEN:
      return PayrollRunStatus.IN_PREPARATION
    case PayrollRunStatus.IN_PREPARATION:
      return PayrollRunStatus.CALCULATION
    case PayrollRunStatus.CALCULATION:
      return PayrollRunStatus.IN_REVIEW
    case PayrollRunStatus.IN_REVIEW:
      return PayrollRunStatus.COMPLETED
    case PayrollRunStatus.COMPLETED:
      return PayrollRunStatus.COMPLETED
    default:
      // Default to the first transition if status is unknown
      return PayrollRunStatus.IN_PREPARATION
  }
}

export const isPayrollRunLocked = (
  status: string | undefined,
  lockedAt: string | null | undefined
): boolean => {
  if (lockedAt != null) {
    return true
  }

  const isOpenStatus = status === PayrollRunStatus.OPEN || status === LegacyPayrollRunStatus.OPENED

  return !isOpenStatus
}
